import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaPlay, FaCheckCircle, FaTimesCircle, FaEdit, FaSave, FaTimes, FaHardHat } from "react-icons/fa";
import api from "../../../libs/axios";
import { useAuth } from "../../../provider/auth.provider";
import { useConfirm } from "../../../provider/confirm.provider";

// The field work is the container every visit hangs off. Starting one moves the
// site to Site Accepted; completing one is the ONLY way the site reaches Work
// Completed, because more visits may still be added right up until it closes.
//
// Until this panel existed the portal had no way to complete a field work, so a
// site could never advance past Work In Progress without going through the app.
interface FieldWorkPanelProps {
  siteId: string;
  // Refresh the site header/stepper after a lifecycle change.
  onWorkflowChange?: () => void;
  // True once billing has been finalized. A locked site is frozen, so the whole
  // field-work lifecycle becomes read-only — the API refuses these calls with
  // 423 anyway, and showing dead buttons is worse than showing none.
  locked?: boolean;
  // Bumped by the Visits tab after a visit changes, so the counts and the
  // guide below re-read (creating a visit can auto-start a field work).
  refreshKey?: number;
  // Tells the Visits tab whether a new visit may be created right now. It may
  // not when the site's latest field work was cancelled and nothing is open:
  // the API refuses to add a visit under a cancelled field work, and will not
  // quietly start a new one behind it either.
  onVisitCreationBlocked?: (reason: string | null) => void;
}

const STATUS_TONE: Record<string, string> = {
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const dateTimeText = (v?: string) => {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d.getTime())
    ? "-"
    : d.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const fullName = (u?: any) => (u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() : "");

// The three-step guide at the top of the panel.
const GuideStep: React.FC<{ n: number; label: string; state: "done" | "current" | "todo" }> = ({ n, label, state }) => (
  <div className="flex items-center gap-2">
    <span
      className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 ${
        state === "done" ? "bg-green-500 text-white" : state === "current" ? "bg-amber-500 text-white" : "bg-gray-200 text-gray-500"
      }`}
    >
      {state === "done" ? <FaCheckCircle className="w-3 h-3" /> : n}
    </span>
    <span className={`text-sm ${state === "todo" ? "text-gray-400" : "text-gray-800 font-medium"}`}>{label}</span>
  </div>
);

// Why a new visit cannot be created, from the site's field works (newest
// first, as the API returns them). Null when it can.
const visitBlockReason = (rows: any[]): string | null => {
  if (rows.some((r) => r.field_work_status === "in_progress")) return null;
  const latest = rows[0];
  if (latest?.field_work_status !== "cancelled") return null;
  return "The last field work was cancelled. Start a new field work before creating a visit.";
};

const FieldWorkPanel: React.FC<FieldWorkPanelProps> = ({
  siteId,
  onWorkflowChange,
  locked = false,
  refreshKey = 0,
  onVisitCreationBlocked,
}) => {
  const { hasPermission } = useAuth();
  const { prompt } = useConfirm();
  const canView = hasPermission("IncSite", "field_work");
  const canStart = hasPermission("IncSite", "field_work_start") && !locked;
  const canEdit = hasPermission("IncSite", "field_work_edit") && !locked;
  const canComplete = hasPermission("IncSite", "field_work_complete") && !locked;
  const canCancel = hasPermission("IncSite", "field_work_cancel") && !locked;

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [startRemarks, setStartRemarks] = useState("");
  const [editing, setEditing] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ remarks: "", work_summary: "" });

  const load = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get(`/inc/sites/${siteId}/field-works`);
      const list: any[] = res.data?.data || [];
      setRows(list);
      onVisitCreationBlocked?.(visitBlockReason(list));
    } catch {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
    // onVisitCreationBlocked is a state setter in the parent — stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, canView]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (!canView) return null;

  const open = rows.find((r) => r.field_work_status === "in_progress");

  // Where the site is in the field phase, for the guide.
  const openVisitCount = open
    ? (open.visits || []).filter((v: any) => v.visit_status !== "completed" && v.visit_status !== "cancelled").length
    : 0;
  const openTotalVisits = open ? (open.visits || []).length : 0;
  const anyCompleted = rows.some((r) => r.field_work_status === "completed");
  // Newest first. A cancelled field work is closed for good: no visit can be
  // added to it, so the next step is always to start another one.
  const lastCancelled = !open && rows[0]?.field_work_status === "cancelled";
  const guide: { states: Array<"done" | "current" | "todo">; hint: string } = lastCancelled
    ? {
        states: ["current", "todo", "todo"],
        hint: canStart
          ? "The last field work was cancelled. Start a new field work — a visit cannot be created under a cancelled one."
          : "The last field work was cancelled. A new field work has to be started before any visit can be created.",
      }
    : open
    ? openTotalVisits === 0
      ? { states: ["done", "current", "todo"], hint: "Field work is open. Add the first visit below." }
      : openVisitCount > 0
        ? {
            states: ["done", "current", "todo"],
            hint: `${openVisitCount} visit${openVisitCount === 1 ? " is" : "s are"} still open — check out or cancel ${openVisitCount === 1 ? "it" : "them"}, then complete the field work.`,
          }
        : { states: ["done", "done", "current"], hint: "All visits are closed. Complete the field work to move the site to Work Completed." }
    : anyCompleted
      ? { states: ["done", "done", "done"], hint: "Field work completed. Start another only if more site work is needed." }
      : { states: ["current", "todo", "todo"], hint: "Start the field work — or simply create a visit below, which starts one for you." };

  const refresh = () => {
    load();
    onWorkflowChange?.();
  };

  const start = async () => {
    setBusy(true);
    try {
      await api.post(`/inc/sites/${siteId}/field-works`, { remarks: startRemarks.trim() || undefined });
      setStartRemarks("");
      refresh();
    } catch {
      /* the axios interceptor already surfaced the message */
    } finally {
      setBusy(false);
    }
  };

  const complete = async (id: number) => {
    const summary = await prompt({
      title: "Complete field work",
      message:
        "Completing the field work closes the field phase and moves the site to Work Completed. Every visit under it must already be checked out or cancelled.",
      label: "Work summary",
      placeholder: "What was achieved in this field work (optional)",
      multiline: true,
      maxLength: 5000,
      confirmText: "Complete field work",
      variant: "info",
    });
    // null means dismissed — an empty string is a deliberate "no summary".
    if (summary === null) return;
    setBusy(true);
    try {
      const res = await api.post(`/inc/field-works/${id}/complete`, {
        work_summary: summary.trim() || undefined,
      });
      const data = res.data?.data;
      if (data) {
        toast.success(
          `Field work completed — ${data.completed_visits} visit(s) completed, ${data.cancelled_visits} cancelled`,
        );
      }
      refresh();
    } catch {
      /* the axios interceptor already surfaced the message */
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id: number) => {
    const reason = await prompt({
      title: "Cancel field work",
      message: "Cancelling does not move the site workflow backwards, but the reason is recorded against it.",
      label: "Reason for cancelling",
      placeholder: "Why is this field work being cancelled?",
      required: true,
      multiline: true,
      maxLength: 2000,
      confirmText: "Cancel field work",
      cancelText: "Keep it open",
      variant: "danger",
    });
    if (reason === null) return;
    if (!reason.trim()) return toast.error("A reason is required to cancel a field work");
    setBusy(true);
    try {
      await api.post(`/inc/field-works/${id}/cancel`, { cancel_reason: reason.trim() });
      refresh();
    } catch {
      /* the axios interceptor already surfaced the message */
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (id: number) => {
    setBusy(true);
    try {
      await api.put(`/inc/field-works/${id}`, {
        remarks: editForm.remarks,
        work_summary: editForm.work_summary,
      });
      setEditing(null);
      load();
    } catch {
      /* the axios interceptor already surfaced the message */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-3 mb-4 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <FaHardHat className="w-3.5 h-3.5 text-amber-600" /> Field Work
        </h3>
        <p className="text-xs text-gray-400">
          Visits are recorded under a field work. Completing it is what moves the site to Work Completed.
        </p>
      </div>

      {!loading && (
        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 mb-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <GuideStep n={1} label="Start field work" state={guide.states[0]} />
            <span className="hidden sm:block w-6 h-px bg-gray-300" />
            <GuideStep n={2} label="Record visits" state={guide.states[1]} />
            <span className="hidden sm:block w-6 h-px bg-gray-300" />
            <GuideStep n={3} label="Complete field work" state={guide.states[2]} />
          </div>
          <p className="text-xs text-gray-600 mt-1.5">{guide.hint}</p>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : (
        <>
          {locked && (
            <p className="text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded px-3 py-2 mb-3">
              Billing has been finalized for this site — the field work is read-only.
            </p>
          )}
          {/* Start — only when nothing is open, mirroring the server's one-open-per-site rule. */}
          {canStart && !open && (
            <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-3 flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-gray-500 mb-1">Remarks (optional)</label>
                <input
                  value={startRemarks}
                  onChange={(e) => setStartRemarks(e.target.value)}
                  className="w-full px-2 py-2 text-sm border border-gray-300 rounded"
                />
              </div>
              <button
                onClick={start}
                disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-60"
              >
                <FaPlay className="w-3 h-3" /> Start Field Work
              </button>
            </div>
          )}
          {canStart && open && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
              Field work is already in progress — complete or cancel it before starting another.
            </p>
          )}

          {rows.length === 0 ? (
            <p className="text-sm text-gray-500">
              No field work started yet.{canStart ? "" : " You do not have permission to start one."}
            </p>
          ) : (
            <div className="space-y-2">
              {rows.map((fw) => {
                const isOpen = fw.field_work_status === "in_progress";
                const visitCount = (fw.visits || []).length;
                const openVisits = (fw.visits || []).filter(
                  (v: any) => v.visit_status !== "completed" && v.visit_status !== "cancelled",
                ).length;
                return (
                  <div key={fw.id} className="border border-gray-200 rounded p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        {/* No "#n" here: a site has one field work at a time, so the
                            number was noise on screen. It is still on the record and in
                            every export. */}
                        <span className="font-medium text-gray-800">Field Work</span>
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${STATUS_TONE[fw.field_work_status] || "bg-gray-100 text-gray-700"}`}
                        >
                          {(fw.field_work_status || "").replace(/_/g, " ")}
                        </span>
                        <span className="text-xs text-gray-500">
                          {visitCount} visit{visitCount === 1 ? "" : "s"}
                          {openVisits > 0 ? ` · ${openVisits} still open` : ""}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {canEdit && isOpen && editing !== fw.id && (
                          <button
                            onClick={() => {
                              setEditing(fw.id);
                              setEditForm({ remarks: fw.remarks || "", work_summary: fw.work_summary || "" });
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-700 border border-blue-200 bg-blue-50 rounded hover:bg-blue-100"
                          >
                            <FaEdit className="w-3 h-3" /> Edit
                          </button>
                        )}
                        {canComplete && isOpen && (
                          <button
                            onClick={() => complete(fw.id)}
                            disabled={busy || visitCount === 0 || openVisits > 0}
                            title={
                              visitCount === 0
                                ? "Add at least one visit first"
                                : openVisits > 0
                                  ? `${openVisits} visit(s) still open — check out or cancel them first`
                                  : "Complete this field work and move the site to Work Completed"
                            }
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-green-700 rounded hover:bg-green-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            <FaCheckCircle className="w-3 h-3" /> Complete Field Work
                          </button>
                        )}
                        {canCancel && isOpen && (
                          <button
                            onClick={() => cancel(fw.id)}
                            // Mirrors the API: a field work with a planned or
                            // in-progress visit cannot be cancelled.
                            disabled={busy || openVisits > 0}
                            title={
                              openVisits > 0
                                ? `${openVisits} visit(s) still planned or in progress — check out or cancel them first`
                                : undefined
                            }
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-700 border border-red-200 bg-red-50 rounded hover:bg-red-100 disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <FaTimesCircle className="w-3 h-3" /> Cancel
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2 text-xs">
                      <div>
                        <div className="text-gray-400 uppercase tracking-wide">Started</div>
                        <div className="text-gray-700">{dateTimeText(fw.started_at)}</div>
                        <div className="text-gray-500">{fullName(fw.starter)}</div>
                      </div>
                      {fw.completed_at && (
                        <div>
                          <div className="text-gray-400 uppercase tracking-wide">Completed</div>
                          <div className="text-gray-700">{dateTimeText(fw.completed_at)}</div>
                          <div className="text-gray-500">{fullName(fw.completer)}</div>
                        </div>
                      )}
                      {fw.cancelled_at && (
                        <div>
                          <div className="text-gray-400 uppercase tracking-wide">Cancelled</div>
                          <div className="text-gray-700">{dateTimeText(fw.cancelled_at)}</div>
                          <div className="text-gray-500 break-words">{fw.cancel_reason || "-"}</div>
                        </div>
                      )}
                    </div>

                    {editing === fw.id ? (
                      <div className="mt-2 flex flex-wrap gap-2 items-end">
                        <div className="flex-1 min-w-[160px]">
                          <label className="block text-xs text-gray-500 mb-1">Remarks</label>
                          <input
                            value={editForm.remarks}
                            onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
                            className="w-full px-2 py-2 text-sm border border-gray-300 rounded"
                          />
                        </div>
                        <div className="flex-1 min-w-[160px]">
                          <label className="block text-xs text-gray-500 mb-1">Work summary</label>
                          <input
                            value={editForm.work_summary}
                            onChange={(e) => setEditForm({ ...editForm, work_summary: e.target.value })}
                            className="w-full px-2 py-2 text-sm border border-gray-300 rounded"
                          />
                        </div>
                        <button
                          onClick={() => saveEdit(fw.id)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-800 rounded hover:bg-blue-900 disabled:opacity-60"
                        >
                          <FaSave className="w-3 h-3" /> Save
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                        >
                          <FaTimes className="w-3 h-3" /> Cancel
                        </button>
                      </div>
                    ) : (
                      (fw.remarks || fw.work_summary) && (
                        <div className="mt-2 text-xs text-gray-600 space-y-0.5">
                          {fw.remarks && (
                            <div>
                              <span className="font-semibold text-gray-500">Remarks: </span>
                              {fw.remarks}
                            </div>
                          )}
                          {fw.work_summary && (
                            <div>
                              <span className="font-semibold text-gray-500">Work summary: </span>
                              {fw.work_summary}
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FieldWorkPanel;
